import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Briefcase, Search, ArrowRight, Filter, FolderKanban, DollarSign, Clock, Plus, Lock, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { StatusBadge, useStatusLabel } from "@/components/StatusBadge";
import { CountdownTimer } from "@/components/CountdownTimer";
import { planFor, isAtLimit, effectiveTier } from "@/lib/plans";
import { cn } from "@/lib/utils";


export const Route = createFileRoute("/_authenticated/projects/")({
  component: ProjectsPage,
  head: () => ({
    meta: [{ title: "Projects · Lumvio" }, { name: "description", content: "All your Lumvio projects in one place." }],
  }),
});

type Project = {
  id: string;
  title: string;
  description: string | null;
  client_email: string;
  budget: number | null;
  status: "brief" | "in_progress" | "in_review" | "completed";
  created_at: string;
  freelancer_id: string;
  deadline: string | null;
  completed_at: string | null;
  duration_days: number | null;
};

type Filter = "all" | "brief" | "in_progress" | "in_review" | "completed";

function ProjectsPage() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const isFreelancer = profile?.role === "freelancer";
  const label = useStatusLabel();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);


  const tier = effectiveTier(profile);
  const used = profile?.projects_created_this_month ?? 0;
  const bonus = profile?.projects_bonus_slots ?? 0;
  const plan = planFor(tier);
  const kyc = profile?.kyc_status ?? "not_started";
  const isDuplicate = (profile?.kyc_duplicate as boolean | undefined) ?? false;
  // ID verification is now a universal gate on project creation for every plan.
  const needsKyc = kyc !== "verified";
  const atLimit = isAtLimit(tier, used, bonus) || needsKyc || (tier === "free" && isDuplicate);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects", "all", user?.id, profile?.role, profile?.email],
    enabled: !!user && !!profile,
    queryFn: async () => {
      let q = supabase.from("projects").select("*").eq("is_intro", false).order("created_at", { ascending: false });
      if (isFreelancer) q = q.eq("freelancer_id", user!.id);
      else q = q.ilike("client_email", profile!.email ?? "");
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Project[];
    },

  });

  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return projects.filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (!s) return true;
      return (
        p.title.toLowerCase().includes(s) ||
        (p.description ?? "").toLowerCase().includes(s) ||
        p.client_email.toLowerCase().includes(s)
      );
    });
  }, [projects, filter, search]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: projects.length, brief: 0, in_progress: 0, in_review: 0, completed: 0 };
    for (const p of projects) c[p.status] = (c[p.status] ?? 0) + 1;
    return c;
  }, [projects]);

  const filterTabs: { key: Filter; label: string }[] = [
    { key: "all", label: t("projectsPage.filters.all", { defaultValue: "All" }) },
    { key: "brief", label: label("brief") },
    { key: "in_progress", label: label("in_progress") },
    { key: "in_review", label: label("in_review") },
    { key: "completed", label: label("completed") },
  ];

  return (
    <div className="relative mx-auto max-w-6xl space-y-6 px-4 py-6 sm:py-8">
      {/* Aurora hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-primary/10 via-background to-background p-6 sm:p-8">
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-primary/8 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              <FolderKanban className="h-3 w-3" /> {t("projectsPage.badge", { defaultValue: "Projects" })}
            </div>
            <h1 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold tracking-tight">
              {t("projectsPage.title", { defaultValue: "All projects" })}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("projectsPage.subtitle", {
                defaultValue: isFreelancer
                  ? "Every engagement you've launched, in one place."
                  : "Every project a freelancer has started with you.",
              })}
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:items-end">
            <div className="grid grid-cols-3 gap-3 sm:flex sm:items-center sm:gap-4">
              <StatTile icon={Briefcase} label={t("projectsPage.stats.total", { defaultValue: "Total" })} value={counts.all} />
              <StatTile icon={Clock} label={label("in_progress")} value={counts.in_progress} tone="amber" />
              <StatTile icon={DollarSign} label={label("completed")} value={counts.completed} tone="emerald" />
            </div>
            {isFreelancer && (
              <NewProjectTrigger onOpen={() => setCreateOpen(true)} atLimit={atLimit} />
            )}
          </div>
        </div>
      </section>

      {/* Create-project dialog (freelancers only) */}
      {isFreelancer && (
        <NewProjectDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          userId={user!.id}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ["projects"] });
            navigate({ to: "/projects/$id", params: { id } });
          }}
        />
      )}

      {/* Filters */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {filterTabs.map((tab) => {
            const active = filter === tab.key;
            const n = counts[tab.key];
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                  active
                    ? "border-primary/60 bg-primary/15 text-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.08)]"
                    : "border-border/60 bg-background text-muted-foreground hover:border-border hover:text-foreground"
                )}
              >
                {tab.label}
                <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-semibold", active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground")}>
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("projectsPage.searchPlaceholder", { defaultValue: "Search projects…" })}
            className="ps-9"
          />
        </div>
      </section>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-40 animate-pulse rounded-xl border border-border/60 bg-muted/40" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/70 bg-muted/20 p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Filter className="h-6 w-6" />
          </div>
          <h3 className="mt-4 font-serif text-lg font-semibold">
            {projects.length === 0
              ? t("projectsPage.empty.title", { defaultValue: "No projects yet" })
              : t("projectsPage.empty.filtered", { defaultValue: "No projects match your filters" })}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {projects.length === 0
              ? t("projectsPage.empty.subtitle", { defaultValue: "Start a project from the dashboard to see it here." })
              : t("projectsPage.empty.clear", { defaultValue: "Try adjusting the search or filter above." })}
          </p>
          {projects.length === 0 && (
            isFreelancer ? (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02]"
              >
                {atLimit ? <Lock className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {t("projectsPage.empty.createCta", { defaultValue: "Create your first project" })}
              </button>
            ) : (
              <Link
                to="/dashboard"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-transform hover:scale-[1.02]"
              >
                {t("projectsPage.empty.cta", { defaultValue: "Go to dashboard" })}
                <ArrowRight className="h-4 w-4" />
              </Link>
            )
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone = "primary" }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number; tone?: "primary" | "amber" | "emerald" }) {
  const toneCls =
    tone === "amber"
      ? "text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20"
      : tone === "emerald"
      ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
      : "text-primary bg-primary/10 border-primary/20";
  return (
    <div className={cn("flex items-center gap-2.5 rounded-xl border px-3 py-2", toneCls)}>
      <Icon className="h-4 w-4" />
      <div className="leading-tight">
        <div className="text-lg font-semibold tabular-nums">{value}</div>
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      </div>
    </div>
  );
}

function ProjectCard({ project }: { project: Project }) {
  const { t } = useTranslation();
  return (
    <Link
      to="/projects/$id"
      params={{ id: project.id }}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_10px_40px_-15px_hsl(var(--primary)/0.35)]"
    >
      <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-32 w-32 rounded-full bg-primary/10 opacity-0 blur-3xl transition-opacity group-hover:opacity-100" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-serif text-base font-semibold group-hover:text-primary transition-colors">
            {project.title}
          </h3>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{project.client_email}</p>
        </div>
        <StatusBadge status={project.status} />
      </div>
      {project.description && (
        <p className="relative mt-3 line-clamp-2 text-sm text-muted-foreground">{project.description}</p>
      )}
      <div className="relative mt-3">
        <CountdownTimer deadline={project.deadline} completedAt={project.completed_at} status={project.status} compact />
      </div>
      <div className="relative mt-3 flex items-center justify-between border-t border-border/50 pt-3 text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <DollarSign className="h-3.5 w-3.5" />
          {project.budget ? `$${Number(project.budget).toLocaleString()}` : t("projectsPage.card.noBudget", { defaultValue: "No budget" })}
        </span>
        <span className="inline-flex items-center gap-1 text-primary opacity-70 transition-all group-hover:opacity-100 group-hover:gap-1.5">
          {t("projectsPage.card.open", { defaultValue: "Open" })}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}

function NewProjectTrigger({ onOpen, atLimit }: { onOpen: () => void; atLimit: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-all",
        "bg-primary text-primary-foreground hover:scale-[1.02]",
        "shadow-[0_0_0_1px_color-mix(in_oklch,var(--primary)_25%,transparent),0_10px_30px_-10px_color-mix(in_oklch,var(--primary)_50%,transparent)]",
      )}
    >
      {atLimit ? <Lock className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
      {t("dashboard.freelancer.newProject", { defaultValue: "New project" })}
    </button>
  );
}

function NewProjectDialog({
  open,
  onOpenChange,
  userId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  userId: string;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [budget, setBudget] = useState("");
  const [duration, setDuration] = useState("7");


  const create = useMutation({
    mutationFn: async () => {
      const emailNorm = clientEmail.trim().toLowerCase();
      const days = Math.max(1, Number(duration) || 1);
      const now = new Date();
      const deadline = new Date(now.getTime() + days * 86400 * 1000);
      const { data: created, error } = await supabase
        .from("projects")
        .insert({
          freelancer_id: userId,
          title,
          description: description || null,
          client_email: emailNorm,
          budget: budget ? Number(budget) : null,
          duration_days: days,
          start_date: now.toISOString(),
          deadline: deadline.toISOString(),
        })
        .select("id")
        .single();
      if (error) throw error;
      const amt = budget ? Number(budget) : 0;
      if (amt > 0) {
        const { error: invErr } = await supabase.from("invoices").insert({
          project_id: created.id,
          freelancer_id: userId,
          amount: amt,
          description: title,
          status: "pending",
        });
        if (invErr) console.warn("Auto-invoice failed", invErr);
      }
      try {
        const { inviteClientToProject } = await import("@/lib/project-notify.functions");
        await inviteClientToProject({ data: { projectId: created.id } });
      } catch (e) {
        console.warn("Client invitation email failed", e);
      }
      return created.id as string;
    },
    onSuccess: (id) => {
      toast.success(t("dashboard.freelancer.create.success", { defaultValue: "Project created" }));
      import("@/lib/posthog").then(({ capturePostHog, PHEvents }) =>
        capturePostHog(PHEvents.ProjectCreated, { project_id: id }),
      ).catch(() => {});
      setTitle(""); setDescription(""); setClientEmail(""); setBudget(""); setDuration("7");
      onOpenChange(false);
      onCreated(id);
    },
    onError: (e: Error) => {
      const msg = e.message ?? "";
      if (msg.includes("KYC_REQUIRED")) {
        toast.error(t("dashboard.freelancer.errors.kyc", { defaultValue: "Verify your identity to create projects." }));
      } else if (msg.includes("DUPLICATE_KYC_UPGRADE_REQUIRED")) {
        toast.error(t("dashboard.freelancer.errors.duplicate", { defaultValue: "Upgrade required to continue." }));
      } else if (msg.includes("PROJECT_LIMIT_REACHED")) {
        toast.error(t("dashboard.freelancer.errors.limit", { defaultValue: "Monthly project limit reached — upgrade your plan." }));
      } else {
        toast.error(msg);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("dashboard.freelancer.create.title", { defaultValue: "Start a new project" })}</DialogTitle>
          <DialogDescription>
            {t("dashboard.freelancer.create.subtitle", { defaultValue: "Invite your client and set the scope. They'll get an email to join." })}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="np-title">{t("dashboard.freelancer.create.projectTitle", { defaultValue: "Project title" })}</Label>
            <Input id="np-title" required value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-desc">{t("dashboard.freelancer.create.description", { defaultValue: "Description" })}</Label>
            <Textarea id="np-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="np-email">{t("dashboard.freelancer.create.clientEmail", { defaultValue: "Client email" })}</Label>
              <Input id="np-email" type="email" required value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-budget">{t("dashboard.freelancer.create.budget", { defaultValue: "Price (USD)" })}</Label>
              <Input id="np-budget" type="number" min="1" step="1" required value={budget} onChange={(e) => setBudget(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="np-duration">{t("dashboard.freelancer.create.duration", { defaultValue: "Duration (days)" })}</Label>
            <Input id="np-duration" type="number" min="1" step="1" required value={duration} onChange={(e) => setDuration(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              {t("dashboard.freelancer.create.durationHint", { defaultValue: "A live countdown will start immediately and stop when you mark the project completed." })}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending
                ? t("common.creating", { defaultValue: "Creating…" })
                : (<><Sparkles className="h-4 w-4" /> {t("dashboard.freelancer.create.submit", { defaultValue: "Create project" })}</>)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
